import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Checkbox from 'components/Checkbox/Checkbox';
import Textarea from 'components/Textarea/Textarea';
import TextField from 'components/TextField/TextField';
import {
  COMMENT, ITEMS, PRIORITIZED_OPTIONS, SELECTED_OPTION_ID, SELECTED_OPTION_IDS, TEXT_ANSWER,
} from 'constants/formFields';
import { SURVEY_QUESTION_TYPES } from 'constants/materialAssessment';

import DraggableList from '../../../../components/DraggableList/DraggableList';
import { DESCRIPTION_LENGTH } from '../../../../constants/general';
import SurveyAnswerItemPanel from './SurveyAnswerItemPanel';

import type { ISurveyAnswerQuestion, PrioritizedOptionItem } from '../interfaces/ISurveyAnswerQuestion';

import styles from '../scss/SurveyAnswerQuestion.module.scss';

function SurveyAnswerQuestion({ control, index, question }: ISurveyAnswerQuestion) {
  const { t } = useTranslation();

  if (!question) {
    return null;
  }

  const renderAnswerField = () => {
    switch (question.questionType) {
      case SURVEY_QUESTION_TYPES.SHORT_ANSWER:
        return (
          <Controller
            control={control}
            name={`${ITEMS}.${index}.${TEXT_ANSWER}`}
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
              <TextField
                name={name}
                value={value || ''}
                label={`${question.name}?`}
                error={error?.message}
                className={styles.textField}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.enterYourAnswer')}
              />
            )}
          />
        );

      case SURVEY_QUESTION_TYPES.PARAGRAPH:
        return (
          <Controller
            control={control}
            name={`${ITEMS}.${index}.${TEXT_ANSWER}`}
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
              <Textarea
                name={name}
                value={value || ''}
                label={`${question.name}?`}
                error={error?.message}
                className={styles.textField}
                placeholder={t('materialityAssessment.placeholder.enterYourAnswer')}
                charactersLimit={DESCRIPTION_LENGTH}
                onChange={(e) => onChange(e.target.value.slice(0, DESCRIPTION_LENGTH))}
              />
            )}
          />
        );

      case SURVEY_QUESTION_TYPES.SINGLE_SELECT:
        return (
          <Controller
            control={control}
            name={`${ITEMS}.${index}.${SELECTED_OPTION_ID}`}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <div className={styles.optionsContainer}>
                <div className={styles.optionsLabel}>
                  {`${question.name}? (${t('materialityAssessment.chooseOne')})`}
                </div>
                {error?.message ? (
                  <div className={styles.optionsError}>
                    {error.message ? t('materialityAssessment.selectIsRequired') : null}
                  </div>
                ) : null}
                <div
                  className={styles.radioContainer}
                  role="radiogroup"
                  aria-invalid={Boolean(error)}
                >
                  {question.options.map((option) => (
                    <label
                      key={option.id}
                      className={styles.optionItem}
                      htmlFor={`option-${question.id}-${option.id}`}
                    >
                      <input
                        id={`option-${question.id}-${option.id}`}
                        type="radio"
                        name={`question-${question.id}`}
                        value={option.id}
                        checked={value === option.id}
                        onChange={() => onChange(option.id)}
                        className={styles.radioInput}
                      />
                      <span>{option.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          />
        );

      case SURVEY_QUESTION_TYPES.MULTIPLE_SELECT:
        return (
          <Controller
            control={control}
            name={`${ITEMS}.${index}.${SELECTED_OPTION_IDS}`}
            render={({ field: { value = [], onChange }, fieldState: { error } }) => {
              const selectedIds = value || [];

              const handleCheckboxChange = (optionId: number, checked: boolean) => {
                if (checked) {
                  const addedValueIds = selectedIds.includes(optionId)
                    ? selectedIds
                    : [...selectedIds, optionId];
                  onChange(addedValueIds);
                  return;
                }

                const updatedValueIds = selectedIds.filter((id) => id !== optionId);
                onChange(updatedValueIds);
              };
              return (
                <div className={styles.optionsContainer}>
                  <div className={styles.optionsLabel}>
                    {`${question.name}? (${t('materialityAssessment.selectAllThatApply')})`}
                  </div>
                  {error?.message ? (
                    <div className={styles.optionsError}>
                      {error.message ? t('materialityAssessment.selectIsRequired') : null}
                    </div>
                  ) : null}
                  {question.options.map((option) => (
                    <Checkbox
                      key={option.id}
                      name={`multiple-${question.id}-${option.id}`}
                      value={option.id.toString()}
                      label={option.name}
                      className={styles.checkboxItem}
                      checked={selectedIds.includes(option.id)}
                      onChange={(event) => handleCheckboxChange(option.id, event.target.checked)}
                    />
                  ))}
                </div>
              );
            }}
          />
        );

      case SURVEY_QUESTION_TYPES.PRIORITIZE:
        return (
          <Controller
            control={control}
            name={`${ITEMS}.${index}.${PRIORITIZED_OPTIONS}`}
            render={({ field: { value, onChange } }) => {
              const prioritizedOptions: PrioritizedOptionItem[] = (() => {
                if (value && value.length > 0) {
                  return value
                    .map((item: { optionId: number, priority: number }, idx: number) => {
                      const option = question.options.find((
                        opt: { id: number, name: string },
                      ) => opt.id === item.optionId);
                      return {
                        optionId: item.optionId,
                        priority: idx + 1,
                        name: option ? option.name : '',
                        id: `${item.optionId}-${idx}`,
                      };
                    });
                }

                const initialOptions = question.options.map((option, idx) => ({
                  optionId: option.id,
                  priority: idx + 1,
                  name: option.name,
                  id: `${option.id}-${idx}`,
                }));

                const initialValue = question.options.map((option, idx) => ({
                  optionId: option.id,
                  priority: idx + 1,
                }));
                onChange(initialValue);

                return initialOptions;
              })();

              const handleMove = (dragIndex: number, hoverIndex: number) => {
                const newOptions = [...prioritizedOptions];
                const draggedItem = newOptions[dragIndex];
                newOptions.splice(dragIndex, 1);
                newOptions.splice(hoverIndex, 0, draggedItem);

                const updatedOptions = newOptions.map((item, idx) => ({
                  optionId: item.optionId,
                  priority: idx + 1,
                }));
                onChange(updatedOptions);
              };

              return (
                <div className={styles.optionsContainer}>
                  <div>
                    <span>{question.name}</span>
                  </div>
                  <p className={styles.dragTitle}>{t('materialityAssessment.dragAndDrop')}</p>
                  <DraggableList
                    items={prioritizedOptions}
                    onMove={handleMove}
                    renderItem={(item) => (
                      <div className={styles.prioritiseOptionContent}>
                        <span className={styles.optionName}>
                          {item.name}
                        </span>
                      </div>
                    )}
                    itemClassName={styles.prioritiseItem}
                    itemType="PRIORITISE_OPTION"
                    keyExtractor={(field: PrioritizedOptionItem) => field.id}
                  />
                </div>
              );
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <SurveyAnswerItemPanel
      title={`${t('materialityAssessment.fields.question')} ${index + 1}`}
    >
      <div
        id={`survey-question-${index}`}
        className={styles.questionWrapper}
      >
        {renderAnswerField()}

        {question.isCommentable ? (
          <Controller
            control={control}
            name={`${ITEMS}.${index}.${COMMENT}`}
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
              <Textarea
                name={name}
                value={value || ''}
                label={t('materialityAssessment.fields.justifyAnswer')}
                error={error?.message}
                placeholder={t('materialityAssessment.placeholder.enterYourAnswer')}
                charactersLimit={DESCRIPTION_LENGTH}
                className={styles.commentField}
                onChange={(e) => onChange(e.target.value.slice(0, DESCRIPTION_LENGTH))}
              />
            )}
          />
        ) : null}
      </div>
    </SurveyAnswerItemPanel>
  );
}

export default SurveyAnswerQuestion;
